import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role: "recruiter",
  profile_image: "",
};

const isAdministrator = (user) =>
  ["admin", "administrator", "system_user"].includes(String(user?.role || "").trim().toLowerCase());

export default function AddUserDialog({ open, onClose, user, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [state, setState] = useState({ saving: false, error: "" });
  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const handleClose = () => {
    if (state.saving) return;
    setState({ saving: false, error: "" });
    setForm(EMPTY_FORM);
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAdministrator(user)) {
      setState({ saving: false, error: "Only administrators can add users." });
      return;
    }

    setState({ saving: true, error: "" });
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
      const response = await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Unable to create user.");
      await onCreated?.(payload.user);
      handleClose();
    } catch (error) {
      setState({ saving: false, error: error?.message || "Unable to create user." });
    }
  };

  if (!isAdministrator(user)) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box component="form" method="post" onSubmit={handleSubmit}>
        <DialogTitle data-popup-header sx={{ fontWeight: 800 }}>Add new user</DialogTitle>
        <DialogContent sx={{ pt: "10px !important" }}>
          {state.error ? <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert> : null}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField required label="Full name" value={form.name} onChange={update("name")} />
            <TextField required type="email" label="Email" value={form.email} onChange={update("email")} />
            <TextField required type="password" label="Password" helperText="At least 6 characters" value={form.password} onChange={update("password")} />
            <TextField required select label="Role" value={form.role} onChange={update("role")}>
              <MenuItem value="administrator">Administrator</MenuItem><MenuItem value="system_user">System</MenuItem>
              <MenuItem value="recruiter">Recruiter</MenuItem>
            </TextField>
            <TextField label="Profile image URL" value={form.profile_image} onChange={update("profile_image")} />
          </Box>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} disabled={state.saving}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={state.saving}>{state.saving ? "Creating..." : "Add user"}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
